import React, {useEffect} from 'react';
import {Box} from 'src/design/atoms/Box';
import {useParams} from 'react-router-dom';
import {FeatureFlagEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkTraining} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {CourseVideoTable} from 'src/app/course-video/CourseVideoTable/CourseVideoTable';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {TrainingLandingTabs} from 'src/pages/Modules/Training/shared/TrainingLandingTabs';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {TrainingDashViewType} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import ListCourseDefinitions from './components/ListCourseDefinitions/ListCourseDefinitions';
import ListUserTrainings from './components/ListUserTrainings/ListUserTrainings';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const KNOWN_VIEWS: TrainingDashViewType[] = ['user', 'manage', 'course-video'];

const TrainingDashPage: React.FC = () => {
  useSetDocumentTitle(TrainingDocumentTitle);

  const trainingPerms = useModulePermissions(ModuleEnum.Training);

  const navigate = useAppNavigate();
  const {view} = useParams();

  // Bookmarks/links carrying an unrecognized view fall back to a
  // permission-aware landing: training admins go to the Compliance
  // cockpit, learners go to My Training.
  useEffect(() => {
    if (view && !KNOWN_VIEWS.includes(view as TrainingDashViewType)) {
      navigate(
        trainingPerms.view
          ? PathUtility.TrainingCompliance()
          : PathUtility.TrainingDashboard('user'),
        {replace: true}
      );
    }
  }, [view, trainingPerms.view, navigate]);

  const activeView = (view ?? 'user') as TrainingDashViewType;

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkTraining.icon}>
          Training
        </PageHeader.Title>
      </PageHeader>
      <TrainingLandingTabs active={activeView} />
      <Box>
        {activeView === 'manage' ? (
          <ListCourseDefinitions />
        ) : activeView === 'course-video' ? (
          <CourseVideoTable identifier="course-video-table" />
        ) : (
          <ListUserTrainings />
        )}
      </Box>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingDashboard(':view'),
  element: <TrainingDashPage />,
  isPrivate: true,
});

export default TrainingDashPage;
